import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router'
import { ArrowLeft, Loader2, PartyPopper, FlaskConical, User, Zap } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  useSimulationCampaign,
  useNextSimulationEntries,
  useUpdateSimulationEntry,
  useTriggerSimulationCall,
  type SimulationEntry,
} from '../api/simulations'
import { CampaignProgress } from '../components/campaigns/CampaignProgress'
import type { CampaignListStats } from '../api/campaigns'
import { DispositionPicker } from '../components/campaigns/DispositionPicker'
import { SimulationCallStatusPanel } from '../components/simulations/SimulationCallStatusPanel'
import { useTelemetryStore } from '../stores/telemetryStore'

type Phase = 'loading' | 'ready' | 'triggering' | 'calling' | 'disposition' | 'complete'

export function SimulationRunnerPage() {
  const { t } = useTranslation('simulations')
  const { id } = useParams()
  const campaignId = id ? Number(id) : undefined
  const navigate = useNavigate()

  const { data: campaign, refetch: refetchCampaign } = useSimulationCampaign(campaignId)
  const { data: nextData, refetch: refetchNext } = useNextSimulationEntries(campaignId, 6)
  const updateEntry = useUpdateSimulationEntry()
  const triggerCall = useTriggerSimulationCall()

  const [currentEntry, setCurrentEntry] = useState<SimulationEntry | null>(null)
  const [entryIndex, setEntryIndex] = useState(0)
  const [phase, setPhase] = useState<Phase>('loading')
  const [activeCallId, setActiveCallId] = useState<string | null>(null)
  const [callListenUrl, setCallListenUrl] = useState<string | undefined>()
  const [callControlUrl, setCallControlUrl] = useState<string | undefined>()

  const activeCalls = useTelemetryStore((s) => s.activeCalls)
  const activeCall = activeCallId ? activeCalls[activeCallId] : undefined

  // Load first entry when data arrives
  useEffect(() => {
    if (!nextData || currentEntry) return
    if (nextData.entries.length > 0) {
      // FOLLOW-UP(react-compiler): local state synced from the SSE telemetry store.
      // It has multiple writers (this effect plus user event handlers), so deriving
      // it during render is a state-machine redesign, not a mechanical fix — and the
      // UI has no JS test suite to catch a regression. Suppressed, not fixed.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCurrentEntry(nextData.entries[0])
      setEntryIndex((campaign?.stats.contacted ?? 0) + 1)
      setPhase('ready')
    } else if (campaign && campaign.stats.remaining === 0 && campaign.stats.total > 0) {
      setPhase('complete')
    }
  }, [nextData, currentEntry, campaign])

  // Detect call started → transition to calling phase
  useEffect(() => {
    if (activeCall && (phase === 'triggering' || phase === 'ready')) {
      // FOLLOW-UP(react-compiler): local state synced from the SSE telemetry store.
      // It has multiple writers (this effect plus user event handlers), so deriving
      // it during render is a state-machine redesign, not a mechanical fix — and the
      // UI has no JS test suite to catch a regression. Suppressed, not fixed.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPhase('calling')
    }
  }, [activeCall, phase])

  // Detect call ended → transition to disposition phase
  useEffect(() => {
    if (activeCallId && !activeCall && phase === 'calling') {
      // FOLLOW-UP(react-compiler): local state synced from the SSE telemetry store.
      // It has multiple writers (this effect plus user event handlers), so deriving
      // it during render is a state-machine redesign, not a mechanical fix — and the
      // UI has no JS test suite to catch a regression. Suppressed, not fixed.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPhase('disposition')
    }
  }, [activeCall, activeCallId, phase])

  const advanceToNext = useCallback(async () => {
    setCurrentEntry(null)
    setActiveCallId(null)
    setCallListenUrl(undefined)
    setCallControlUrl(undefined)
    setPhase('loading')
    await refetchCampaign()
    await refetchNext()
  }, [refetchCampaign, refetchNext])

  const handleTriggerCall = async () => {
    if (!currentEntry || !campaignId) return
    setPhase('triggering')
    try {
      const result = await triggerCall.mutateAsync({
        campaignId,
        entryId: currentEntry.id,
      })
      setActiveCallId(result.call_id)
      setCallListenUrl(result.listen_url || undefined)
      setCallControlUrl(result.control_url || undefined)
    } catch {
      setPhase('ready')
    }
  }

  const handleDisposition = async (disposition: string, notes: string) => {
    if (!currentEntry) return
    await updateEntry.mutateAsync({
      entryId: currentEntry.id,
      status: 'called',
      disposition,
      notes,
      called_at: Date.now() / 1000,
    })
    advanceToNext()
  }

  const handleSkip = async () => {
    if (!currentEntry) return
    await updateEntry.mutateAsync({
      entryId: currentEntry.id,
      status: 'skipped',
    })
    advanceToNext()
  }

  if (!campaignId) {
    navigate('/app/simulations')
    return null
  }

  return (
    <div className="space-y-5">
      {/* Back link */}
      <Link
        to={`/app/simulations/${id}`}
        className="inline-flex items-center gap-1.5 text-sm text-text-secondary transition-colors hover:text-primary"
      >
        <ArrowLeft className="h-4 w-4" />
        {t('runner.back')}
      </Link>

      {/* Progress header */}
      {campaign && (
        <CampaignProgress name={campaign.name} stats={campaign.stats as CampaignListStats} />
      )}

      {/* Loading state */}
      {phase === 'loading' && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}

      {/* Ready / Calling / Disposition states */}
      {currentEntry && phase !== 'loading' && (
        <div className="space-y-4">
          {/* Simulation entry card */}
          <SimulationEntryCard
            entry={currentEntry}
            index={entryIndex}
            total={campaign?.stats.total ?? 0}
          />

          {/* Start simulation button (when ready) */}
          {phase === 'ready' && (
            <div className="flex items-center gap-3">
              <button
                onClick={handleTriggerCall}
                className="inline-flex items-center gap-2 rounded-lg bg-info px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-info/80"
              >
                <Zap className="h-4 w-4" />
                {t('runner.startSimulation')}
              </button>
              <button
                onClick={handleSkip}
                className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-text-secondary transition-colors hover:bg-surface-elevated"
              >
                {t('runner.skip')}
              </button>
            </div>
          )}

          {/* Triggering state */}
          {phase === 'triggering' && !activeCallId && (
            <div className="flex items-center gap-3 rounded-lg border border-info/30 bg-info/5 px-4 py-3">
              <Loader2 className="h-5 w-5 animate-spin text-info" />
              <span className="text-sm font-medium text-text-primary">
                {t('runner.starting')}
              </span>
            </div>
          )}

          {/* Call status panel — live status, listen + barge-in */}
          {activeCallId && (phase === 'triggering' || phase === 'calling' || phase === 'disposition') && (
            <SimulationCallStatusPanel
              callId={activeCallId}
              listenUrl={callListenUrl}
              controlUrl={callControlUrl}
            />
          )}

          {/* Disposition picker — shown after call ends */}
          {phase === 'disposition' && (
            <DispositionPicker
              onSave={handleDisposition}
              onSkip={handleSkip}
              isSubmitting={updateEntry.isPending}
            />
          )}
        </div>
      )}

      {/* Campaign complete */}
      {phase === 'complete' && (
        <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-surface-card py-16">
          <PartyPopper className="h-12 w-12 text-success" />
          <h2 className="mt-4 text-xl font-bold text-text-primary">{t('runner.complete')}</h2>
          <p className="mt-2 text-sm text-text-secondary">
            {t('runner.completeBody', { count: campaign?.stats.total ?? 0 })}
          </p>
          <Link
            to={`/app/simulations/${id}`}
            className="mt-6 rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary/90"
          >
            {t('runner.backToCampaign')}
          </Link>
        </div>
      )}

      {/* Upcoming queue */}
      {nextData && phase !== 'complete' && (
        <SimulationUpcomingQueue
          entries={nextData.entries}
          currentEntryId={currentEntry?.id}
        />
      )}
    </div>
  )
}

// ── Sub-components ─────────────────────────────────────────────────────

function SimulationEntryCard({
  entry,
  index,
  total,
}: {
  entry: SimulationEntry
  index: number
  total: number
}) {
  const { t } = useTranslation('simulations')
  return (
    <div className="rounded-lg border border-border bg-surface-card p-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-info/10">
            <FlaskConical className="h-5 w-5 text-info" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-text-primary">
              {entry.simulation_name}
            </h3>
            <div className="flex items-center gap-3 text-sm text-text-secondary">
              <span className="inline-flex items-center gap-1">
                <User className="h-3.5 w-3.5" />
                {entry.personality_name}
              </span>
              <span>·</span>
              <span>{entry.scenario_name}</span>
            </div>
          </div>
        </div>
        <span className="rounded-full bg-surface-elevated px-3 py-1 text-xs font-medium text-text-muted">
          {t('runner.indexOfTotal', { index, total })}
        </span>
      </div>

      {entry.description && (
        <div className="mt-4 rounded-lg bg-surface-elevated p-3">
          <p className="text-xs text-text-secondary">{entry.description}</p>
        </div>
      )}
    </div>
  )
}

function SimulationUpcomingQueue({
  entries,
  currentEntryId,
}: {
  entries: SimulationEntry[]
  currentEntryId?: number
}) {
  const { t } = useTranslation('simulations')
  const upcoming = entries.filter((e) => e.id !== currentEntryId)
  if (upcoming.length === 0) return null

  return (
    <div className="rounded-lg border border-border bg-surface-card p-4">
      <h3 className="text-sm font-semibold text-text-primary">{t('runner.upNext')}</h3>
      <ul className="mt-3 divide-y divide-border">
        {upcoming.map((entry, i) => (
          <li key={entry.id} className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-surface-elevated text-xs font-medium text-text-muted">
              {i + 1}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-text-primary">
                {entry.simulation_name}
              </p>
              <p className="flex items-center gap-1 text-xs text-text-muted">
                <User className="h-3 w-3" />
                {entry.personality_name}
                <span className="mx-1">·</span>
                {entry.scenario_name}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}
